import { useCallback, useEffect, useRef, useState } from "react";
import Drill from "@drillflo/drillcore/lib/Drill";
import { toast } from "react-hot-toast";
import View from "@drillflo/drillcore/lib/2d/View.ts";

export interface PlaybackData {
  isPlaying: boolean;
  currentPage: number;
  currentCount: number;
  setIsPlaying: (isPlaying: boolean) => void;
  setCurrentPage: (page: number) => void;
  setCurrentCount: (count: number) => void;
}

export default function usePlayback(
  drill: Drill | null,
  view: View | null,
  /** Increments on remote drill updates; used only as an effect dependency. */
  drillModifiedByCollaboration?: number | boolean
) {
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const animationRef = useRef<number>();
  const lastFrameTimeRef = useRef<number>(0);
  const animationStartTimeRef = useRef<number>(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const [currentCount, setCurrentCount] = useState<number>(0);
  const [playbackState, setPlaybackState] = useState<PlaybackData>(() => {
    return {
      isPlaying: false,
      currentPage: 0,
      currentCount: 0,
      setIsPlaying: () => { },
      setCurrentPage: () => { },
      setCurrentCount: () => { },
    };
  });

  (window as any).$playbackState = playbackState;

  useEffect(() => {
    (window as any).$playbackState = playbackState;
  }, [playbackState]);

  const audioFileFromDrill = drill?.getCustomData("audioFile") as string | undefined;

  useEffect(() => {
    if (audioFileFromDrill) {
      setAudioUrl(audioFileFromDrill);
    } else {
      setAudioUrl(null);
    }
  }, [audioFileFromDrill, drill, drillModifiedByCollaboration]);

  useEffect(() => {
    if (audioUrl) {
      // Pause and reset audio before loading new source
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      audioRef.current.src = audioUrl;
      audioRef.current.load();
    }
  }, [audioUrl]);

  const handleCountChange = useCallback(
    (count: number) => {
      if (drill && drill.engine && view) {
        const pageCounts = drill.pages[drill.selectedPageIndex + 1]?.counts || 0;

        const clampedCount = Math.max(0, Math.min(count, pageCounts));

        const transitionPercentageToSet = (clampedCount / pageCounts) * 100;

        if (transitionPercentageToSet > 100) {
          view.setPageTransitionPercentage(100);

        } else if (transitionPercentageToSet > 0) {
          view.setPageTransitionPercentage(transitionPercentageToSet);
        }
        setPlaybackState((prev) => ({ ...prev, currentCount: clampedCount }));
      }
    },
    [drill, view]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (drill) {
        if (page < 0) {
          page = 0;
        }
        if (page > drill.pages.length - 1) {
          page = drill.pages.length - 1;
        }
        setPlaybackState((prev) => ({
          ...prev,
          currentPage: page,
          currentCount: 0,
        }));

        drill.selectPage(page);
      } else {
        toast.error("No drill");
      }
    },
    [drill]
  );

  const animate = useCallback(
    (currentTime: number) => {
      if (!playbackState.isPlaying === false || !drill || !view)
        return (
          toast.error("Something went wrong"),
          console.log("drill", drill),
          console.log("view", view),
          console.log("playbackState", playbackState)
        );

      // Use date timestamp for smoother animation
      const now = Date.now();
      const deltaTime = lastFrameTimeRef.current
        ? (now - lastFrameTimeRef.current) / 1000
        : 0;
      lastFrameTimeRef.current = now;

      // Access current and next page data
      const currentPageIndex = drill.selectedPageIndex;
      const nextPageIndex = currentPageIndex + 1;

      // If we're on page 0, use page 1's tempo and counts
      const effectivePageIndex = currentPageIndex + 1;
      const currentPageData = drill?.pages[effectivePageIndex];
      const currentPageTempo = drill?.pages[currentPageIndex + 1]?.tempo ?? 120;
      const currentPageCounts =
        drill?.pages[currentPageIndex + 1]?.counts ||
          currentPageIndex !== drill?.pages.length
          ? drill?.pages[currentPageIndex + 1]?.counts
          : 0;

      const countsPerSecond = currentPageTempo / 60;
      const countIncrement = deltaTime * countsPerSecond;

      setCurrentCount((prevCount) => {
        const newCount = prevCount + countIncrement;
        setPlaybackState((prev) => ({ ...prev, currentCount: newCount }));

        if (!currentPageCounts) return 0;
        if (newCount >= currentPageCounts) {
          if (
            nextPageIndex < drill.pages.length - 1 &&
            effectivePageIndex !== drill.pages.length
          ) {
            // Disable drawing until the next page is loaded and transitioned
            view.getDrill().disableDraw = true;
            // drill.disableDraw = true;
            // Preload next page asynchronously if needed
            view.getDrill().selectPage(nextPageIndex);

            setPlaybackState((prev) => ({
              ...prev,
              currentPage: nextPageIndex,
            }));
            view.setPageTransitionPercentage(0);

            view.getDrill().disableDraw = false;
            // drill.disableDraw = false;

            return 0; // Reset count for the new page
          } else {
            // Stop playback
            setPlaybackState((prev) => ({ ...prev, isPlaying: false }));

            handlePageChange(drill.pages.length - 1);

            view.setPageTransitionPercentage(0);
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
            }
            if (animationRef.current) {
              cancelAnimationFrame(animationRef.current);
            }

            return 0;
          }
        }

        // Update transition percentage smoothly
        view.setPageTransitionPercentage((newCount / currentPageCounts) * 100);
        return newCount;
      });

      animationRef.current = requestAnimationFrame(animate);
    },
    [playbackState.isPlaying, drill]
  );

  const handlePlayDrill = () => {
    if (!drill || !drill.engine) return toast.error("Something went wrong");
    drill.engine.movementArrows = false;
    drill.engine.clearSelection();
    setPlaybackState((prev) => ({ ...prev, isPlaying: true }));
    // setCurrentCount(0);
    lastFrameTimeRef.current = 0;

    // Calculate time offset for audio based on current position in the show
    let totalTimeBeforeCurrentPage = 0;
    const currentPageIndex = drill.selectedPageIndex;

    if (currentPageIndex > 0) {
      for (let i = 0; i < currentPageIndex; i++) {
        if (!drill) return;
        const pageTempo = drill.pages[i + 1]?.tempo || 120;
        const pageTime =
          ((drill.pages[i + 1]?.counts + currentCount || 0) / pageTempo) * 60;
        totalTimeBeforeCurrentPage += pageTime;
      }
    }

    if (audioRef.current) {
      // Pause any existing playback first to avoid interrupting play() calls
      audioRef.current.pause();
      audioRef.current.currentTime = totalTimeBeforeCurrentPage;

      // Wait a tick to ensure pause completes before playing
      Promise.resolve().then(() => {
        if (audioRef.current) {
          const playPromise = audioRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              // Only log if it's not an AbortError (which is expected when paused)
              if (error.name !== "AbortError") {
                console.warn("Audio autoplay prevented:", error);
              }
            });
          }
        }
      });
    }

    animationRef.current = requestAnimationFrame(animate);

    // logEvent(analytics, "play_drill_document", {
    //   played_at: Timestamp.now().toDate().toISOString(),
    //   page_viewed: "/designs",
    //   user: userData?.id,
    //   drill_doc: slug,
    //   device_type: navigator.userAgent.includes("Mobi") ? "mobile" : "desktop",
    // });
  };

  const handleStopDrill = () => {
    if (!drill || !view || !drill.engine)
      return toast.error("Something went wrong");
    drill.engine.movementArrows = true;
    setPlaybackState((prev) => ({ ...prev, isPlaying: false }));
    setCurrentCount(0);
    view.setPageTransitionPercentage(0);
    drill.disableDraw = false;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    // logEvent(analytics, "stop_drill_document", {
    //     stopped_at: Timestamp.now().toDate().toISOString(), // Log as ISO string for consistency
    //     page_viewed: "/designs",
    //     user: userData?.id,
    //     drill_doc: slug,
    //     device_type: navigator.userAgent.includes("Mobi") ? "mobile" : "desktop",
    // });
  };

  return {
    playbackState,
    setPlaybackState,
    handlePageChange,
    handleCountChange,
    handlePlayDrill,
    handleStopDrill,
    currentCount,
  };
}
